import type { ResponseCursors } from '@/types';
import type { PaginationModel } from '@montugroup/design-system';
import { useCallback, useEffect, useState } from 'react';
import { useGetPatientNotes } from './useGetPatientNotes';

export interface PatientNotes {
  id: number;
  note: string;
  heading: string;
  strike_reason: string | null;
  created_by: number | undefined;
  created_date: string;
  patient_id: number;
  CreatedBy: {
    email: string | undefined;
    first_name: string | null | undefined;
    last_name: string | null | undefined;
  };
}
export type SortType = [{ id: string; desc: boolean }];
export const usePatientNotes = (patientId: number) => {
  const [notesCursors, setNotesCursors] = useState<ResponseCursors>({ prev: null, next: null });
  const [notesSearch, setNotesSearch] = useState<string>('');
  const [notesSort, setNotesSort] = useState<string>('-createdDate');
  const [responseNotesCursors, setResponseNotesCursors] = useState<ResponseCursors>();
  const [patientNotesData, setPatientNotesData] = useState<PatientNotes[]>();
  const notesPageParams = {
    size: 5,
    after: notesCursors?.next ?? undefined,
    before: notesCursors?.prev ?? undefined,
  };
  const {
    data: patientNotes,
    isLoading: isPatientNotesLoading,
    refetch: refetchNotes,
    isError: isPatientNotesError,
  } = useGetPatientNotes(notesPageParams, notesSearch, notesSort, patientId);

  const onSort = useCallback((sort: { id: string; desc: boolean }[]): void => {
    if (sort.length > 0) {
      const sortQuery = sort?.[0]?.id ?? 'createdDate';
      const direction = sort?.[0]?.desc === false ? 'asc' : 'desc';
      setNotesCursors?.({ prev: null, next: null });
      setNotesSort?.(direction === 'desc' ? `-${sortQuery}` : `${sortQuery}`);
    }
  }, []);

  const handleNotesPaginationChange = ({ page: isNewPage }: PaginationModel) => {
    const cursorKey = isNewPage > 0 ? 'next' : 'prev';
    if (responseNotesCursors?.[cursorKey]) {
      if (cursorKey === 'next') {
        setNotesCursors({ prev: null, next: responseNotesCursors[cursorKey] });
      } else {
        setNotesCursors({ prev: responseNotesCursors[cursorKey], next: null });
      }
    }
  };

  useEffect(() => {
    if (patientNotes && !isPatientNotesLoading) {
      const notes = patientNotes?.data?.map((note) => ({
        id: note.id,
        note: note.attributes.note,
        heading: note.attributes.heading,
        strike_reason: note.attributes.strikeReason,
        created_by: note.attributes.createdBy?.userId,
        created_date: note.attributes.createdDate,
        patient_id: note.attributes.patientId,
        CreatedBy: {
          email: note.attributes.createdBy?.email,
          first_name: note.attributes.createdBy?.firstName,
          last_name: note.attributes.createdBy?.lastName,
        },
      }));
      setPatientNotesData(notes);
      setResponseNotesCursors(patientNotes.meta?.page?.cursor);
    }
  }, [patientNotes, isPatientNotesLoading]);

  return {
    patientNotesData,
    responseNotesCursors,
    onSort,
    refetchNotes,
    setNotesCursors,
    handleNotesPaginationChange,
    setNotesSearch,
    isPatientNotesError,
  };
};
